// frontend_app/src/services/api.js

import { API_BASE_URL } from '../config';
import { readSseStream } from '../lib/sse';

export class ApiError extends Error {
  constructor(message, status = 0) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

const readErrorDetail = async (response, fallback) => {
  try {
    const data = await response.json();
    if (typeof data.detail === 'string') return data.detail;
  } catch (e) {
    /* not JSON */
  }
  return fallback;
};

/**
 * fetch() with the session cookie and CSRF header. A 401 announces
 * "lsearch-auth-expired" so the app can return to the sign-in screen.
 */
const request = async (path, { method = 'GET', body, signal, suppressAuthEvent = false } = {}) => {
  let response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      method,
      credentials: 'include',
      signal,
      headers: {
        'X-LSearch-Requested-With': 'XMLHttpRequest',
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (e) {
    if (e.name === 'AbortError') throw e;
    throw new ApiError('Could not reach the server. Check your connection and try again.', 0);
  }
  if (response.status === 401 && !suppressAuthEvent) {
    window.dispatchEvent(new CustomEvent('lsearch-auth-expired'));
  }
  return response;
};

const requestJSON = async (path, options = {}, fallbackMessage = 'Something went wrong.') => {
  const response = await request(path, options);
  if (!response.ok) {
    throw new ApiError(await readErrorDetail(response, fallbackMessage), response.status);
  }
  return response.json();
};

// ── Authentication ─────────────────────────────────────────────────────────

export const login = async (username, password) => {
  const response = await request('/login', {
    method: 'POST', body: { username, password }, suppressAuthEvent: true,
  });
  if (!response.ok) {
    const fallback = response.status === 429
      ? 'Too many failed attempts. Try again in 15 minutes.'
      : 'Sign-in failed.';
    throw new ApiError(await readErrorDetail(response, fallback), response.status);
  }
};

export const logout = async () => {
  await request('/logout', { method: 'POST', suppressAuthEvent: true }).catch(() => {});
};

export const verify = async () => {
  const response = await request('/verify', { suppressAuthEvent: true });
  if (!response.ok) throw new ApiError('Session is not valid', response.status);
  const data = await response.json();
  return data.user;
};

// ── Search ─────────────────────────────────────────────────────────────────

/**
 * Stream a federated search. `onPackage` receives one per-source package as
 * it arrives. Resolves when the server sends "done"; rejects with ApiError
 * (e.g. 400 query too short, 403 live search not permitted) or AbortError.
 */
export const streamSearch = async ({
  query, sources, customFolders, mode, fullText, signal, onPackage,
}) => {
  const params = new URLSearchParams({ query, search_mode: mode === 'live' ? 'live' : 'local' });
  if (mode === 'live') params.append('use_cache', 'true');
  if (sources && sources.length) params.append('sources', sources.join(','));
  if (customFolders && customFolders.length) params.append('custom_folders', customFolders.join(','));
  if (fullText && mode !== 'live') params.append('deep_search', 'true');

  const response = await request(`/stream-federated-search?${params.toString()}`, { signal });
  if (!response.ok) {
    throw new ApiError(await readErrorDetail(response, 'The search could not be started.'), response.status);
  }

  let finished = false;
  await readSseStream(response, ({ event, data }) => {
    if (event === 'done') {
      finished = true;
      return;
    }
    if (!data) return;
    try {
      onPackage(JSON.parse(data));
    } catch (e) {
      // A malformed frame should not abort the whole search.
      console.error('Could not parse search result frame', e);
    }
  });
  if (!finished) {
    throw new ApiError('The connection closed before the search finished.', 0);
  }
};

export const fetchSources = async (signal) => {
  const data = await requestJSON('/sources', { signal }, 'Could not load the list of labs.');
  return data.sources || [];
};

export const fetchSuggestions = async (term, signal) => {
  const params = new URLSearchParams({ q: term });
  const data = await requestJSON(`/suggest?${params.toString()}`, { signal }, 'Could not load suggestions.');
  return data.suggestions || [];
};

export const fetchDetails = async (url, source, signal) => {
  const params = new URLSearchParams({ url, source });
  const data = await requestJSON(`/fetch-details?${params.toString()}`, { signal }, 'Could not load details.');
  return data.details || {};
};

// ── Admin: data sources & builders ─────────────────────────────────────────

export const fetchAdminSources = () =>
  requestJSON('/admin/sources', {}, 'Could not load data sources.');

export const fetchJobs = async () => {
  const data = await requestJSON('/admin/jobs', {}, 'Could not load builder status.');
  return data.jobs || {};
};

export const runBuilder = (builderName) =>
  requestJSON(`/admin/run-builder/${encodeURIComponent(builderName)}`, { method: 'POST' }, 'Could not start the builder.');

export const updateSchedule = (builderName, intervalDays) =>
  requestJSON(`/admin/schedule/${encodeURIComponent(builderName)}`, {
    method: 'POST', body: { interval_days: intervalDays },
  }, 'Could not save the schedule.');

// ── Admin: users ───────────────────────────────────────────────────────────

export const fetchUsers = async () => {
  const data = await requestJSON('/admin/users', {}, 'Could not load users.');
  return data.users || [];
};

export const addUser = (user) =>
  requestJSON('/admin/users', { method: 'POST', body: user }, 'Could not create the user.');

export const updateUser = (userId, changes) =>
  requestJSON(`/admin/users/${userId}`, { method: 'PUT', body: changes }, 'Could not update the user.');

export const deleteUser = (userId) =>
  requestJSON(`/admin/users/${userId}`, { method: 'DELETE' }, 'Could not delete the user.');
